/**
 * Chinese characters to the pinyin they might be read as.
 *
 * **This path is dormant.** As of the prerequisite check in BHCS-100, 0 of 267 active
 * students have a Chinese name stored — not in `first_name`, not in `last_name`, not
 * anywhere in `students.metadata`. Nothing in the portal collects 中文姓名 today, so this
 * file has no input and contributes nothing to the live matcher or to the harness's headline
 * numbers. It is built so that collecting the field is the only work left, and it is
 * measured separately (`--fixtures=han`) so the day the field exists we already know what it
 * buys.
 *
 * The reason it is worth building ahead of the data: heteronyms. A surname is exactly where
 * the non-default reading is often the right one — 乐 is Yue far more often as a surname than
 * Le, 单 is Shan not Dan, 解 is Xie not Jie. A converter that returns one reading per
 * character gets these wrong silently, and a wrong surname is a total match failure rather
 * than a near miss. So every reading is indexed and the roster decides which one was meant.
 *
 * ## Why a table rather than `pinyin-pro` or `pinyinlite`
 *
 * `pinyin-pro` is the better library and is what should be used the day this path goes live:
 * its `multiple` mode returns every reading of a character, it has `pattern: 'initial'` for
 * the initials index for free, and its dictionary covers given-name characters — which are
 * the ones we cannot enumerate by hand, because a given name can be any character in the
 * language. `pinyinlite` is smaller and built around generating pronunciation combinations,
 * but its dictionary is thinner on the rarer characters that turn up in given names, which is
 * where the coverage actually has to be good.
 *
 * Neither is a dependency today, and adding one for a path with zero input rows would be
 * exactly the over-engineering the ticket warns against. What is here instead is the
 * hundred-odd surnames this school actually sees plus every heteronym among them — enough to
 * measure the idea. Swap in `pinyin-pro` when `chinese_name` has rows in it; `readingsFor`
 * is the only function that has to change.
 */

/**
 * Surnames whose surname reading is *not* the character's default reading.
 *
 * These are the ones a naive converter gets wrong, and the reason this file exists. The
 * surname reading is listed first.
 */
const HETERONYM_SURNAMES: Record<string, readonly string[]> = {
  乐: ['yue', 'le'],
  单: ['shan', 'dan'],
  曾: ['zeng', 'ceng'],
  查: ['zha', 'cha'],
  解: ['xie', 'jie'],
  覃: ['qin', 'tan'],
  仇: ['qiu', 'chou'],
  区: ['ou', 'qu'],
  华: ['hua', 'huà'],
  纪: ['ji', 'ji'],
  繁: ['po', 'fan'],
  阚: ['kan', 'han'],
  哈: ['ha', 'ha'],
  折: ['she', 'zhe'],
  员: ['yun', 'yuan'],
  能: ['nai', 'neng'],
  黑: ['he', 'hei'],
  石: ['shi', 'dan'],
  会: ['kuai', 'hui'],
  任: ['ren', 'ren'],
  应: ['ying', 'ying'],
  折冲: ['she', 'zhe'],
  种: ['chong', 'zhong'],
  盖: ['ge', 'gai'],
  莘: ['shen', 'xin'],
  过: ['guo', 'guo'],
  长: ['zhang', 'chang'],
  重: ['chong', 'zhong'],
  朴: ['piao', 'pu'],
  繆: ['miao', 'mou'],
  缪: ['miao', 'mou'],
  燕: ['yan', 'yan'],
  都: ['du', 'dou'],
  谌: ['chen', 'shen'],
  句: ['gou', 'ju'],
  乘: ['sheng', 'cheng'],
};

/** The ordinary surnames — one reading each, no ambiguity to resolve. */
const PLAIN_SURNAMES: Record<string, string> = {
  张: 'zhang', 刘: 'liu', 陈: 'chen', 李: 'li', 杨: 'yang', 黄: 'huang', 赵: 'zhao',
  朱: 'zhu', 王: 'wang', 徐: 'xu', 何: 'he', 江: 'jiang', 吴: 'wu', 曹: 'cao',
  蔡: 'cai', 孙: 'sun', 马: 'ma', 胡: 'hu', 郭: 'guo', 林: 'lin', 高: 'gao',
  罗: 'luo', 郑: 'zheng', 梁: 'liang', 谢: 'xie', 宋: 'song', 唐: 'tang', 冯: 'feng',
  于: 'yu', 董: 'dong', 萧: 'xiao', 程: 'cheng', 邓: 'deng', 彭: 'peng', 薛: 'xue',
  卢: 'lu', 蒋: 'jiang', 沈: 'shen', 韩: 'han', 叶: 'ye', 潘: 'pan', 杜: 'du',
  戴: 'dai', 范: 'fan', 方: 'fang', 施: 'shi', 姚: 'yao', 谭: 'tan', 廖: 'liao',
  邹: 'zou', 邱: 'qiu', 贾: 'jia', 赖: 'lai', 秦: 'qin', 魏: 'wei', 严: 'yan',
  段: 'duan', 雷: 'lei', 侯: 'hou', 白: 'bai', 钱: 'qian', 田: 'tian', 苏: 'su',
  庞: 'pang', 顾: 'gu', 孟: 'meng', 邵: 'shao', 万: 'wan', 齐: 'qi', 尹: 'yin',
  牛: 'niu', 孔: 'kong', 毛: 'mao', 崔: 'cui', 钟: 'zhong', 龙: 'long', 丁: 'ding',
  倪: 'ni', 陶: 'tao', 金: 'jin', 安: 'an', 常: 'chang', 柯: 'ke', 莫: 'mo',
  袁: 'yuan', 詹: 'zhan', 康: 'kang', 傅: 'fu', 盛: 'sheng', 葛: 'ge', 姜: 'jiang',
  周: 'zhou', 许: 'xu', 吕: 'lv', 卜: 'bu', 尚: 'shang', 温: 'wen', 文: 'wen',
};

/**
 * Given-name characters common enough to be worth having, with their heteronyms.
 *
 * Nowhere near complete and not meant to be — a given name can use any character in the
 * language, which is the argument for `pinyin-pro` above rather than for a longer table here.
 */
const GIVEN_NAME_CHARS: Record<string, readonly string[]> = {
  思: ['si'], 远: ['yuan'], 欣: ['xin'], 怡: ['yi'], 伟: ['wei'], 明: ['ming'],
  子: ['zi'], 涵: ['han'], 宇: ['yu'], 轩: ['xuan'], 浩: ['hao'], 然: ['ran'],
  梓: ['zi'], 萱: ['xuan'], 雨: ['yu'], 泽: ['ze'], 一: ['yi'], 诺: ['nuo'],
  嘉: ['jia'], 佳: ['jia'], 悦: ['yue'], 心: ['xin'], 语: ['yu'], 桐: ['tong'],
  睿: ['rui'], 智: ['zhi'], 佩: ['pei'], 芳: ['fang'], 婷: ['ting'], 洁: ['jie'],
  静: ['jing'], 敏: ['min'], 慧: ['hui'], 丽: ['li'], 娟: ['juan'], 燕: ['yan'],
  强: ['qiang'], 磊: ['lei'], 军: ['jun'], 涛: ['tao'], 鑫: ['xin'], 晨: ['chen'],
  阳: ['yang'], 天: ['tian'], 佑: ['you'], 恩: ['en'], 乐: ['le', 'yue'],
  行: ['xing', 'hang'], 长: ['chang', 'zhang'], 重: ['zhong', 'chong'],
  和: ['he', 'huo'], 曲: ['qu', 'qu'], 藏: ['cang', 'zang'], 朝: ['chao', 'zhao'],
  参: ['can', 'shen'], 传: ['chuan', 'zhuan'],
};

/** Every pinyin reading of one character, surname reading first. Empty when unknown. */
export function readingsFor(char: string, position: 'surname' | 'given'): string[] {
  if (position === 'surname') {
    const hetero = HETERONYM_SURNAMES[char];
    if (hetero) return [...hetero];
    const plain = PLAIN_SURNAMES[char];
    if (plain) return [plain];
  }
  const given = GIVEN_NAME_CHARS[char];
  if (given) return [...given];
  const hetero = HETERONYM_SURNAMES[char];
  if (hetero) return [...hetero];
  const plain = PLAIN_SURNAMES[char];
  if (plain) return [plain];
  return [];
}

/** The most readings we will enumerate before giving up on the cartesian product. */
const MAX_VARIANTS = 32;

/**
 * Every way a Chinese name could be read, as syllable arrays.
 *
 * `李思远` → `[["li","si","yuan"]]`. `乐子涵` → `[["yue","zi","han"], ["le","zi","han"]]` —
 * both indexed, because which one the family uses is not something a table can know.
 *
 * The first character is treated as the surname, which is right for a Chinese name written
 * in Chinese order and is the only order this field would ever be collected in.
 */
export function nameVariants(chineseName: string): string[][] {
  const chars = [...chineseName.replace(/\s/g, '')];
  if (chars.length === 0) return [];

  let variants: string[][] = [[]];
  for (let i = 0; i < chars.length; i++) {
    const readings = readingsFor(chars[i], i === 0 ? 'surname' : 'given');
    // An unknown character contributes nothing rather than killing the whole name — a name
    // with one rare character still matches on its other two syllables.
    if (readings.length === 0) continue;
    const next: string[][] = [];
    for (const prefix of variants) {
      for (const reading of readings) {
        if (next.length >= MAX_VARIANTS) break;
        next.push([...prefix, reading]);
      }
    }
    variants = next;
  }

  return variants.filter(v => v.length > 0);
}

/** How much of this table would actually fire, for the harness's coverage report. */
export function tableSize(): { surnames: number; heteronyms: number; givenChars: number } {
  return {
    surnames: Object.keys(PLAIN_SURNAMES).length + Object.keys(HETERONYM_SURNAMES).length,
    heteronyms: Object.keys(HETERONYM_SURNAMES).length,
    givenChars: Object.keys(GIVEN_NAME_CHARS).length,
  };
}
